import React from 'react';
import BaseDialog from './BaseDialog';
import { useTranslation } from 'react-i18next';

// 定义SceneItem类型，用于描述分镜信息
interface SceneItem {
  id: string;
  desc: string;       // 分镜描述
  prompt: string;     // 对应后端的prompt字段
  dialogue: string;   // 对应后端的dialogue字段
  images: string[];
  videoUrl: string;
  audioUrl: string;
}

// 定义ImageSelectionDialog的props接口
interface ImageSelectionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  sceneId: string;
  sceneNumber: number;
  imageId: number;
  scene: SceneItem | null;
}

/**
 * 图片选择对话框组件
 * - 继承BaseDialog的基础样式和布局
 * - 专门用于显示和查看分镜图片
 * - 包含图片信息和图片预览
 */
const ImageSelectionDialog: React.FC<ImageSelectionDialogProps> = ({
  isOpen,
  onClose,
  sceneId,
  sceneNumber,
  imageId,
  scene
}) => {
  const { t } = useTranslation();

  return (
    <BaseDialog
      isOpen={isOpen}
      onClose={onClose}
      title={t('dialog.imageSelection.title', { sceneNumber, imageId })}
      width={600}
      height={500}
    >
      <div className="space-y-4">
        {/* 图片信息 */}
        <div className="flex flex-col gap-2">
          <label className="text-sm text-gray-400">{t('dialog.imageSelection.imageInfo')}</label>
          <div className="p-4 bg-gray-900/80 rounded-lg border border-gray-700/50 text-sm text-white">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-gray-500">{t('dialog.imageSelection.sceneId')}:</span> {sceneId}
              </div>
              <div>
                <span className="text-gray-500">{t('dialog.imageSelection.imageId')}:</span> {imageId}
              </div>
            </div>
            <div className="mt-2">
              <span className="text-gray-500">{t('dialog.imageSelection.imageDescription')}:</span>
              <div className="mt-1 pl-4 border-l border-gray-700/50 italic">
                {scene?.desc || t('dialog.imageSelection.noDescription')}
              </div>
            </div>
            <div className="mt-2">
              <span className="text-gray-500">{t('dialog.imageSelection.prompt')}:</span>
              <div className="mt-1 pl-4 border-l border-gray-700/50 text-xs text-gray-400">
                {scene?.prompt || t('dialog.imageSelection.noPrompt')}
              </div>
            </div>
          </div>
        </div>

        {/* 图片预览 */}
        <div className="flex flex-col gap-2">
          <label className="text-sm text-gray-400">{t('dialog.imageSelection.imagePreview')}</label>
          <div className="p-4 bg-gray-900/80 rounded-lg flex items-center justify-center">
            {scene && scene.images.length > 0 && imageId <= scene.images.length ? (
              <img
                src={scene.images[imageId - 1]}
                alt={t('dialog.imageSelection.imagePlaceholder', { imageId })}
                className="max-w-48 max-h-48 rounded object-contain"
              />
            ) : (
              <div className="w-48 h-48 bg-gray-700/30 rounded flex items-center justify-center text-xs text-gray-300">
                {t('dialog.imageSelection.imagePlaceholder', { imageId })}
              </div>
            )}
          </div>
        </div>

        {/* 图片状态 */}
        <div className="flex flex-col gap-2">
          <label className="text-sm text-gray-400">{t('dialog.imageSelection.imageStatus')}</label>
          <div className="p-3 bg-gray-900/80 rounded-lg border border-gray-700/50 text-sm">
            {scene && scene.images.length > 0 && imageId <= scene.images.length ? (
              <div className="text-green-400 flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {t('dialog.imageSelection.imageGenerated')}
              </div>
            ) : (
              <div className="text-yellow-400 flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {scene ? t('dialog.imageSelection.imageGenerating') : t('dialog.imageSelection.sceneNotFound')}
              </div>
            )}
          </div>
        </div>

        {/* 操作提示 */}
        <div className="text-xs text-gray-500 text-center">
          {t('dialog.imageSelection.tip')}
        </div>
      </div>
    </BaseDialog>
  );
};

export default ImageSelectionDialog;